import { supabase } from "@/integrations/supabase/client";
import { FundRawRecord } from "./types";

// Define types for database records to match actual structure
interface FundsRawDbRecord {
  id: string;
  fund_name: string;
  management_company_code: string;
  company_name: string;
  company_id?: string; // Link to fund_companies table
  fund_code: string;
  currency?: string;
  fund_type?: string;
  category?: string;
  category_group?: string;
  formatted_fund_code?: string;
  sa_asset_class: string;
  sa_sub_asset_class: string;
  management_fee?: number;
  trailer_fee?: number;
  distribution_frequency?: string;
  distribution_options?: string;
  fund_marketing_url?: string;
  created_at?: string;
  updated_at?: string;
  [key: string]: any; // Allow for additional fields
}

interface FundCompanyDbRecord {
  id: string;
  fund_company_name: string;
  website_url: string;
  [key: string]: any;
}

/**
 * Types for query parameters and results
 */
export interface FundsQueryParams {
  page: number;
  pageSize: number;
  searchText?: string;
  fundType?: string | null;
  subAssetClass?: string | null;
  category?: string | null;
}

export interface FundsQueryResult {
  data: FundRawRecord[];
  count: number;
}

/**
 * Main query function to fetch funds with pagination and filtering
 * 
 * This preserves all the nuanced filtering logic from the original FundsRawTable
 * while providing better error handling and consistency with CompanyFundsTable
 */
export async function fetchFunds({
  page,
  pageSize,
  searchText,
  fundType,
  subAssetClass,
  category
}: FundsQueryParams): Promise<FundsQueryResult> {
  console.log("Fetching funds with params:", {
    page, pageSize, searchText, fundType, subAssetClass, category
  });

  try {
    // Start with a base query that fetches funds data
    let query = supabase
      .from("funds_raw")
      .select(`*`, { count: "exact" });

    // Apply filters - preserving exact search behavior for text search
    if (searchText) {
      query = query.or(
        `fund_name.ilike.%${searchText}%,fund_code.ilike.%${searchText}%,company_name.ilike.%${searchText}%`
      );
    }

    // Apply all existing filter logic with the same behavior as before
    // Asset Type filter maps to sa_asset_class column
    if (fundType) query = query.eq("sa_asset_class", fundType);
    if (subAssetClass) query = query.eq("sa_sub_asset_class", subAssetClass);
    if (category) query = query.eq("category", category);

    // Maintain existing pagination approach
    const { data, error, count } = await query
      .range((page - 1) * pageSize, page * pageSize - 1)
      .order("fund_name");

    // Enhanced error logging
    if (error) {
      console.error("Error fetching funds:", error);
      return { data: [], count: 0 };
    }

    // Debug log results
    console.log(`Found ${count || 0} total funds, showing ${data?.length || 0} for page ${page}`);
    
    // Process the data to include company website URLs
    const fundsData = data as FundsRawDbRecord[] || [];
    let processedData = fundsData;
    
    if ((processedData?.length || 0) > 0) {
      // Get all company IDs to fetch their website URLs
      const companyIds = processedData
        .filter(fund => fund.company_id)
        .map(fund => fund.company_id as string);
      
      if ((companyIds?.length || 0) > 0) {
        const { data: companiesData } = await supabase
          .from("fund_companies")
          .select("id, website_url")
          .in("id", companyIds);
          
        // Map website URLs to funds
        if (companiesData && (companiesData?.length || 0) > 0) {
          const typedCompanies = companiesData as FundCompanyDbRecord[];
          
          processedData = processedData.map(fund => {
            // Find matching company
            const company = fund.company_id ? 
              typedCompanies.find(c => c.id === fund.company_id) : undefined;
              
            // Return fund with company website URL
            return {
              ...fund,
              fund_companies: company ? { website_url: company.website_url } : undefined
            };
          });
        }
      }
    }
    
    // Transform processed data to match FundRawRecord interface
    const typedData: FundRawRecord[] = processedData.map(fund => ({
      id: fund.id,
      fund_name: fund.fund_name,
      management_company_code: fund.management_company_code,
      company_name: fund.company_name,
      company_id: fund.company_id,
      fund_code: fund.fund_code,
      currency: fund.currency,
      fund_type: fund.fund_type,
      category: fund.category,
      category_group: fund.category_group,
      formatted_fund_code: fund.formatted_fund_code,
      sa_asset_class: fund.sa_asset_class,
      sa_sub_asset_class: fund.sa_sub_asset_class,
      management_fee: fund.management_fee,
      trailer_fee: fund.trailer_fee,
      distribution_frequency: fund.distribution_frequency,
      distribution_options: fund.distribution_options,
      fund_marketing_url: fund.fund_marketing_url,
      company_website_url: fund.fund_companies?.website_url,
      fund_companies: fund.fund_companies
    }));
    
    return { 
      data: typedData,
      count: count || 0 
    };
  } catch (error) {
    console.error("Exception in fetchFunds:", error);
    return { data: [], count: 0 };
  }
}

/**
 * Helper function to fetch fund types for filtering
 */
export async function fetchFundTypes(): Promise<string[]> {
  try {
    const { data, error } = await supabase
      .from("funds_raw")
      .select("fund_type")
      .not("fund_type", "is", null)
      .order("fund_type");
    
    if (error) {
      console.error("Error fetching fund types:", error);
      return [];
    }
    
    // Extract unique fund types
    const fundTypes = [...new Set(data.map(item => item.fund_type))];
    return fundTypes.filter(Boolean) as string[];
  } catch (error) {
    console.error("Exception in fetchFundTypes:", error);
    return [];
  }
}

/**
 * Helper function to fetch sub-asset classes for filtering
 */
export async function fetchSubAssetClasses(): Promise<string[]> {
  try {
    const { data, error } = await supabase
      .from("funds_raw")
      .select("sa_sub_asset_class")
      .not("sa_sub_asset_class", "is", null)
      .order("sa_sub_asset_class");
    
    if (error) {
      console.error("Error fetching sub-asset classes:", error);
      return [];
    }
    
    // Extract unique sub-asset classes
    const subAssetClasses = [...new Set(data.map(item => item.sa_sub_asset_class))];
    return subAssetClasses.filter(Boolean) as string[];
  } catch (error) {
    console.error("Exception in fetchSubAssetClasses:", error);
    return [];
  }
}

/**
 * Helper function to fetch categories for filtering
 */
export async function fetchCategories(): Promise<string[]> {
  try {
    const { data, error } = await supabase
      .from("funds_raw")
      .select("category")
      .not("category", "is", null)
      .order("category");
    
    if (error) {
      console.error("Error fetching categories:", error);
      return [];
    }
    
    // Extract unique categories
    const categories = [...new Set(data.map(item => item.category))];
    return categories.filter(Boolean) as string[];
  } catch (error) {
    console.error("Exception in fetchCategories:", error);
    return [];
  }
}
